import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminEmbed from './AdminEmbed';
import { AdminHostNavigateMessage } from './adminEmbedProtocol';

export const ADMIN_ROUTE = 'admin/*';
export const ADMIN_BASE = '/admin';
const DEFAULT_EMBED_PATH = 'contracts';

/** URL of an embedded Admin page, e.g. adminEmbedUrl('contracts') → /admin/contracts. */
export const adminEmbedUrl = (embedPath: string = DEFAULT_EMBED_PATH) =>
    `${ADMIN_BASE}/${embedPath}`;

/**
 * Route host for the embedded Blazor Admin app. Derives the iframe's `/embed/*` path from the
 * React route splat (deep-link in) and, when the iframe reports navigation, mirrors it into the
 * React URL/history (deep-link out) so browser back/forward stays consistent. React owns the
 * browser URL; the iframe never touches the top window's history.
 */
const AdminEmbedPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // For a route of "admin/*", params['*'] is the subpath (e.g. "contracts").
    const subPath = params['*']?.replace(/^\/+/, '') || DEFAULT_EMBED_PATH;

    const handleNavigate = useCallback(
        (embedPath: string) => {
            const sub = embedPath.replace(/^\/?embed\/?/, '');
            const next = `${ADMIN_BASE}/${sub}`;
            if (next !== pathname) {
                navigate(next);
            }
        },
        [navigate, pathname]
    );

    // Only paths inside this app: a scheme or a protocol-relative "//host" must not leave it.
    const handleHostNavigate = useCallback(
        (message: AdminHostNavigateMessage) => {
            if (message.path.startsWith('/') && !message.path.startsWith('//')) {
                navigate(message.path);
            }
        },
        [navigate]
    );

    // Fill the area under the Navbar; the iframe stays mounted for the whole /admin/* subtree so
    // the Admin app boots once per visit (visibility can be toggled instead of unmounting when the
    // host grows sibling tabs).
    return (
        <div style={{ position: 'relative', height: 'calc(100vh - 64px)', width: '100%' }}>
            <AdminEmbed
                path={subPath}
                onNavigate={handleNavigate}
                onHostNavigate={handleHostNavigate}
            />
        </div>
    );
};

export default AdminEmbedPage;
